// @ts-nocheck
/**
 * GCP Cloud Storage Service
 * Handles authentication, file uploads, downloads, and signed URLs for user's own GCP project
 * Project: charged-curve-479316-g3
 *
 * PREREQUISITE for /api/sign-asset to work: each matilda-user-projects-*
 * bucket needs CORS configured to allow GET/HEAD from your deploy origins.
 * Set ONCE per bucket with:
 *
 *   gsutil cors set cors.json gs://matilda-user-projects-user_XXXX
 *
 * If models loaded successfully on a given bucket before signed URLs began
 * expiring, CORS is already correct. The re-sign helper below regenerates
 * fresh v4 signatures via /api/sign-asset so URLs stop expiring out from
 * under the user.
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Extract `bucket/object/path.glb` from a fully-qualified GCS URL like:
 *   https://storage.googleapis.com/matilda-user-projects-user_X/models/abc.glb?token=...
 * Returns null if the URL isn't a GCS URL we can re-sign.
 */
export const extractBucketPath = (gcsUrl: string): string | null => {
  try {
    const u = new URL(gcsUrl);
    if (u.hostname !== 'storage.googleapis.com') return null;
    // pathname starts with "/<bucket>/<object/path>"
    const trimmed = u.pathname.replace(/^\/+/, '');
    if (!trimmed.startsWith('matilda-user-projects-')) return null;
    return trimmed;
  } catch {
    return null;
  }
};

/**
 * Request a fresh v4 signed URL for the given bucket path. POSTs to the
 * Node Express /api/sign-asset endpoint which uses a service account to
 * mint the signature. Returns null on failure (caller decides whether to
 * surface an error or fall back).
 *
 * Used by the ModelErrorBoundary retry path: when GLTFLoader fails with a
 * 403/CORS error (the symptom of an expired signed URL), the boundary
 * calls this and re-tries the load with the fresh URL.
 */
export const requestSignedUrlRefresh = async (gcsUrlOrBucketPath: string): Promise<string | null> => {
  // Accept either a full GCS URL or a raw bucket path.
  let bucketPath: string | null;
  if (gcsUrlOrBucketPath.startsWith('http')) {
    bucketPath = extractBucketPath(gcsUrlOrBucketPath);
    if (!bucketPath) return null;
  } else {
    bucketPath = gcsUrlOrBucketPath;
  }

  try {
    const r = await fetch('/api/sign-asset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bucketPath }),
    });
    if (!r.ok) {
      console.warn('[GCP] sign-asset returned', r.status);
      return null;
    }
    const { url } = await r.json();
    return typeof url === 'string' ? url : null;
  } catch (err) {
    console.warn('[GCP] sign-asset request failed:', err);
    return null;
  }
};

interface GCPConfig {
  projectId: string;
  bucketName: string;
  clientId: string;
}

interface UploadResponse {
  path: string;
  signedUrl: string;
  contentType: string;
}

// Initialize with your GCP project config
const GCP_CONFIG: GCPConfig = {
  projectId: '816102132964',
  bucketName: 'matilda-user-projects', // Will be user-scoped
  clientId: (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID) || 'YOUR_CLIENT_ID_HERE.apps.googleusercontent.com'
};

// Store auth token in memory and localStorage
let authToken: string | null = null;

export class GCPService {
  // Store uploaded files in memory for the session
  private static uploadedFiles: Array<{ name: string; path: string; size: number; created: number }> = [];

  /**
   * Initialize Google OAuth flow
   * In production, this uses Google's OAuth 2.0 for user's own GCP project
   */
  static async initializeAuth(): Promise<string> {
    // Check if already authenticated
    const stored = localStorage.getItem('gcp_auth_token');
    if (stored) {
      authToken = stored;
      return authToken;
    }

    // Initialize Google OAuth - This will be implemented with google-auth-library
    // For now, mock implementation with proper structure
    return this.mockGoogleOAuth();
  }

  /**
   * Mock Google OAuth - Replace with real implementation using:
   * import { GoogleAuth } from 'google-auth-library';
   * import { google } from 'googleapis';
   */
  private static async mockGoogleOAuth(): Promise<string> {
    // In production, this would:
    // 1. Open Google OAuth consent screen
    // 2. User authorizes app to access their GCP project
    // 3. Get access token with Cloud Storage scopes
    // 4. Store token securely

    const mockToken = `gcp_token_${uuidv4()}`;
    authToken = mockToken;
    localStorage.setItem('gcp_auth_token', mockToken);
    localStorage.setItem('gcp_user_id', `user_${uuidv4()}`);
    
    return mockToken;
  }

  /**
   * Get the user's GCP bucket name (scoped to their account)
   */
  static async getUserBucket(): Promise<string> {
    const userId = localStorage.getItem('gcp_user_id') || 'default';
    return `${GCP_CONFIG.bucketName}-${userId}`.toLowerCase();
  }

  /**
   * Upload file to GCP Cloud Storage
   * Organizes by type: models/, videos/, images/, projects/
   */
  static async uploadFile(
    file: File,
    type: 'model' | 'video' | 'image' | 'project'
  ): Promise<UploadResponse> {
    if (!authToken) {
      await this.initializeAuth();
    }

    const bucket = await this.getUserBucket();
    const fileName = `${uuidv4()}_${file.name}`;
    const path = `${type}s/${fileName}`; // models/, videos/, images/, projects/
    
    // In production, this uses:
    // const storage = new Storage({ projectId: GCP_CONFIG.projectId });
    // const bucket = storage.bucket(bucketName);
    // const file = bucket.file(path);
    // await file.save(fileBuffer);

    // Mock response with realistic structure
    const mockSignedUrl = `https://storage.googleapis.com/${bucket}/${path}?token=${uuidv4()}`;

    console.log(`[GCP] Uploaded ${file.name} to ${bucket}/${path}`);

    // Store in memory for listing
    this.uploadedFiles.push({
      name: file.name,
      path: `gs://${bucket}/${path}`,
      size: file.size,
      created: Date.now()
    });

    return {
      path: `gs://${bucket}/${path}`,
      signedUrl: mockSignedUrl,
      contentType: file.type
    };
  }

  /**
   * Upload blob as file
   */
  static async uploadBlob(
    blob: Blob,
    filename: string,
    type: 'model' | 'video' | 'image' | 'project',
    contentType: string
  ): Promise<UploadResponse> {
    const file = new File([blob], filename, { type: contentType });
    return this.uploadFile(file, type);
  }

  /**
   * Download file from GCP Cloud Storage
   */
  static async downloadFile(gcpPath: string): Promise<Blob> {
    if (!authToken) {
      await this.initializeAuth();
    }

    // In production: 
    // const storage = new Storage({ projectId: GCP_CONFIG.projectId });
    // const [buffer] = await storage.bucket(bucket).file(path).download();

    // Mock: fetch from signed URL
    const response = await fetch(gcpPath);
    return response.blob();
  }

  /**
   * Get signed URL for file (for embedding in exports, social sharing, etc.)
   * Signed URLs are valid for 7 days by default
   */
  static async getSignedUrl(
    gcpPath: string,
    expirationHours: number = 168
  ): Promise<string> {
    if (!authToken) {
      await this.initializeAuth();
    }

    // In production:
    // const storage = new Storage({ projectId: GCP_CONFIG.projectId });
    // const bucket = storage.bucket(bucketName);
    // const file = bucket.file(path);
    // const [url] = await file.getSignedUrl({
    //   version: 'v4',
    //   action: 'read',
    //   expires: Date.now() + expirationHours * 3600 * 1000,
    // });

    // Mock signed URL - In production, GCP generates time-based signatures
    return `${gcpPath}?expires=${Date.now() + expirationHours * 3600 * 1000}&token=${uuidv4()}`;
  }

  /**
   * List all files of a type in user's bucket
   */
  static async listFiles(type?: 'model' | 'video' | 'image' | 'project'): Promise<Array<{ name: string; path: string; size: number; created: number }>> {
    if (!authToken) {
      await this.initializeAuth();
    }

    const bucket = await this.getUserBucket();

    // In production:
    // const storage = new Storage({ projectId: GCP_CONFIG.projectId });
    // const [files] = await storage.bucket(bucket).getFiles({ prefix: type ? `${type}s/` : undefined });

    // Mock response
    const mockFiles = [
      {
        name: 'sample_model.glb',
        path: `gs://${bucket}/models/sample_model.glb`,
        size: 1024000,
        created: Date.now() - 86400000
      }
    ];

    // Filter by type if provided (simple check on path or name)
    const filteredUploaded = type 
      ? this.uploadedFiles.filter(f => f.path.includes(`/${type}s/`))
      : this.uploadedFiles;

    return [...mockFiles, ...filteredUploaded];
  }

  /**
   * Delete file from GCP Cloud Storage
   */
  static async deleteFile(gcpPath: string): Promise<void> {
    if (!authToken) {
      await this.initializeAuth();
    }

    // In production:
    // const storage = new Storage({ projectId: GCP_CONFIG.projectId });
    // await storage.bucket(bucket).file(path).delete();

    console.log(`[GCP] Deleted ${gcpPath}`);
  }

  /**
   * Save scene configuration to GCP
   */
  static async saveScene(
    sceneName: string,
    sceneData: {
      objects: Record<string, unknown>[];
      lighting: string;
      background: string;
      camera: { position: [number, number, number]; target: [number, number, number] };
      animations?: Record<string, unknown>[];
      materials?: Record<string, unknown>[];
    }
  ): Promise<UploadResponse> {
    const blob = new Blob([JSON.stringify(sceneData, null, 2)], { type: 'application/json' });
    return this.uploadBlob(blob, `${sceneName}.scene.json`, 'project', 'application/json');
  }

  /**
   * Load scene configuration from GCP
   */
  static async loadScene(gcpPath: string): Promise<Record<string, unknown>> {
    const blob = await this.downloadFile(gcpPath);
    const text = await blob.text();
    return JSON.parse(text);
  }

  /**
   * Get authentication status
   */
  static isAuthenticated(): boolean {
    return !!authToken || !!localStorage.getItem('gcp_auth_token');
  }

  /**
   * Logout and clear auth token
   */
  static logout(): void {
    authToken = null;
    localStorage.removeItem('gcp_auth_token');
    localStorage.removeItem('gcp_user_id');
  }

  /**
   * Get current user info
   */
  static getUserInfo(): { id: string; email?: string; name?: string } | null {
    const userId = localStorage.getItem('gcp_user_id');
    if (!userId) return null;
    
    return {
      id: userId,
      email: localStorage.getItem('gcp_user_email') || undefined,
      name: localStorage.getItem('gcp_user_name') || undefined
    };
  }
}

export default GCPService;
